import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from "./controls.js";
import { moveItem } from "./list.js";
import { ICON_CLASS } from "./styles.js";
/**
 * Add, change, move and remove the rows of one list. The registration form's own fields
 * (`FormPage` of the backoffice) use it as well.
 *
 * The focus goes where a keyboard user expects it afterwards: onto the moved row's button,
 * into the new row's first field, onto "hinzufügen" after a removal. At the edge the moving
 * button is not there, so its partner takes the focus; with two rows or more it always is.
 */
export function rowsOf(rows, write, focus, id, parts) {
    return {
        update(key, patch) {
            write((current) => current.map((row) => (row.key === key ? { ...row, ...patch(row) } : row)));
        },
        add(row) {
            focus(id(parts.first, row.key));
            write((current) => [...current, row]);
        },
        move(index, step) {
            const row = rows[index];
            const to = index + step;
            if (row === undefined || to < 0 || to >= rows.length) {
                return;
            }
            const atEdge = step < 0 ? to === 0 : to === rows.length - 1;
            const same = step < 0 ? "hoch" : "runter";
            const other = step < 0 ? "runter" : "hoch";
            focus(id(atEdge ? other : same, row.key));
            write((current) => moveItem(current, index, to));
        },
        remove(index) {
            focus(id(parts.add));
            write((current) => current.filter((_, at) => at !== index));
        },
    };
}
/**
 * A row's number and its buttons, each only where it works: no "nach oben" on the first row, no
 * "nach unten" on the last. The buttons are signs (↑ ↓ ✕), their names carry the row.
 * `fixed` rows show their number only: the publisher keeps their order and number (E6).
 */
export function RowHead({ title, index, count, id, onMove, onRemove, fixed = false, }) {
    const heading = _jsx("h5", { className: "font-sans text-sm font-semibold", children: title });
    if (fixed) {
        return heading;
    }
    return (_jsxs("div", { className: "flex items-center gap-2", children: [heading, _jsxs("div", { className: "ml-auto flex gap-0.5", children: [index === 0 ? null : (_jsx("button", { id: id("hoch"), type: "button", className: ICON_CLASS, "aria-label": `${title} nach oben`, title: "Nach oben", onClick: () => {
                            onMove(-1);
                        }, children: _jsx(Icon, { name: "up" }) })), index === count - 1 ? null : (_jsx("button", { id: id("runter"), type: "button", className: ICON_CLASS, "aria-label": `${title} nach unten`, title: "Nach unten", onClick: () => {
                            onMove(1);
                        }, children: _jsx(Icon, { name: "down" }) })), _jsx("button", { id: id("entfernen"), type: "button", className: ICON_CLASS, "aria-label": `${title} entfernen`, title: "Entfernen", onClick: onRemove, children: _jsx(Icon, { name: "close" }) })] })] }));
}
/**
 * The row an input belongs to, said before its label to screen readers only: "Folie 2, Titel".
 * The row's heading is not read when Tab lands in the row, and up to five inputs share a label.
 * The words are the place a refused save names.
 */
export function InRow({ row }) {
    return _jsxs("span", { className: "sr-only", children: [row, ", "] });
}
//# sourceMappingURL=rows.js.map